import type { PropsWithChildren } from 'react'
import { memo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import clsx from 'clsx'

const ProgressContainer = styled.div`
  position: relative;
  flex-shrink: 0;
`

const SVGContainer = styled.svg`
  transform: rotate(-90deg);
  transform-origin: center;
  position: absolute;
  top: 0;
  inset-inline-start: 0;
  width: 100%;
  height: 100%;

  [dir="rtl"] & {
    transform: rotate(-90deg) scaleX(-1);
  }
`

const CircleBackground = styled.circle`
  &.light {
    stroke: ${props => props.theme.palette.neutral.surface.lightForeground};
  }

  &.onTierBackground.tier-bronze {
    stroke: ${props => props.theme.palette.product.loyalty.bronze.darkContrast};
  }

  &.onTierBackground.tier-silver {
    stroke: ${props => props.theme.palette.product.loyalty.silver.darkContrast};
  }

  &.onTierBackground.tier-gold {
    stroke: ${props => props.theme.palette.product.loyalty.gold.darkContrast};
  }

  &.onTierBackground.tier-platinum {
    stroke: ${props => props.theme.palette.product.loyalty.platinum.normalContrast};
  }
`

const CircleProgress = styled.circle`
  &.light.tier-bronze {
    stroke: ${props => props.theme.palette.product.loyalty.bronze.darkForeground};
  }

  &.light.tier-silver {
    stroke: ${props => props.theme.palette.product.loyalty.silver.darkForeground};
  }

  &.light.tier-gold {
    stroke: ${props => props.theme.palette.product.loyalty.gold.darkForeground};
  }

  &.light.tier-platinum {
    stroke: ${props => props.theme.palette.product.loyalty.platinum.darkForeground};
  }

  &.onTierBackground.tier-bronze {
    stroke: ${props => props.theme.palette.product.loyalty.bronze.darkBackground};
  }

  &.onTierBackground.tier-silver {
    stroke: ${props => props.theme.palette.product.loyalty.silver.darkBackground};
  }

  &.onTierBackground.tier-gold {
    stroke: ${props => props.theme.palette.product.loyalty.gold.darkBackground};
  }

  &.onTierBackground.tier-platinum {
    stroke: ${props => props.theme.palette.product.loyalty.platinum.darkBackground};
  }
`

const ProgressContent = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`

interface Props {
  progress: number
  tier: App.LoyaltyTier
  /**
   * 'light' - for white/neutral backgrounds (neutral bg stroke, tier foreground progress)
   * 'onTierBackground' - for tier-colored backgrounds (tier contrast bg stroke, tier background progress)
   * @default 'light'
   */
  variant?: 'light' | 'onTierBackground'
  /**
   * CSS transition duration for the stroke-dashoffset animation (any valid CSS time, e.g. `0.5s`,
   * `1.2s`, `200ms`, `0s`). Passed as `transition` on the progress circle.
   */
  transitionDuration?: string
  /**
   * @default 76
   */
  size?: number
  /**
   * When set, both rings use this stroke width. When omitted, keeps the account-tile defaults (6 / 7).
   */
  strokeWidth?: number
}

const DEFAULT_SIZE = 76
const DEFAULT_BACKGROUND_STROKE = 6
const DEFAULT_PROGRESS_STROKE = 7

function LoyaltyAccountTileCircularProgressBar(props: PropsWithChildren<Props>) {
  const {
    progress,
    children,
    tier,
    variant = 'onTierBackground',
    transitionDuration = '0.5s',
    size = DEFAULT_SIZE,
    strokeWidth,
  } = props
  const circleCoords = Math.round(size / 2)
  const backgroundStroke = strokeWidth ?? DEFAULT_BACKGROUND_STROKE
  const progressStroke = strokeWidth ?? DEFAULT_PROGRESS_STROKE
  // Custom stroke: fit inside viewBox. Default size keeps legacy radius inset of 5.
  const circleRadius = strokeWidth != null
    ? circleCoords - strokeWidth / 2 - 1
    : circleCoords - 5
  const circumference = 2 * Math.PI * circleRadius
  const offset = circumference - (progress / 100) * circumference
  const tierClass = `tier-${tier}`

  return (
    <ProgressContainer style={{ width: rem(size), height: rem(size) }}>
      <SVGContainer width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <CircleBackground
          className={clsx(variant, tierClass)}
          cx={circleCoords}
          cy={circleCoords}
          r={circleRadius}
          strokeWidth={backgroundStroke}
          fill="none"
        />
        <CircleProgress
          className={clsx(variant, tierClass)}
          cx={circleCoords}
          cy={circleCoords}
          r={circleRadius}
          strokeWidth={progressStroke}
          fill="none"
          strokeDasharray={`${circumference} ${circumference}`}
          strokeLinecap="round"
          strokeDashoffset={offset}
          style={{ transition: `stroke-dashoffset ${transitionDuration} ease` }}
        />
      </SVGContainer>
      {children && <ProgressContent>
        {children}
      </ProgressContent>}
    </ProgressContainer>
  )
}

export default memo(LoyaltyAccountTileCircularProgressBar)
